/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { map, trim, get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { FontIcon } from 'material-ui';

import { getEmployeeStatus, getEmployeeGroup } from 'utils/translations';
import DetailList from '../DetailList';
import { download, getFileById } from '../../actions/Files';
import pallet from '../../constants/colors';

const fix = { overflow: 'visible' }; // fix material-ui https://my.origo.is/browse/APPAIL-7744

const EmployeeCompanyConnectionTable = ({
  employeeCompanyConnection, valuesByKey, language,
}) => {
  const dispatch = useDispatch();
  const { FileReducer } = useSelector(state => state);
  const { fileById } = FileReducer;
  const [fileName, setFileName] = useState(null);
  const [isSharePoint, setIsSharePoint] = useState(null);

  const {
    EmployeeDetailID,
    EmployeeGroup,
    EmployeeGroupName,
    EmployeeStatus,
    EmployeeStatusName,
    ManagerName,
    OrgCompanyName,
    OrgCompanyPositionAlternativeName,
    OrgCompanyPositionName,
    OrgCompanyUnitDivisionName,
    OrgCompanyUnitName,
    OrgLocationMain,
    OrgLocationSub,
    JobDescriptionDocumentID,
  } = employeeCompanyConnection;

  const showJobDescription = () => {
    if (isSharePoint) {
      return (
        <div>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={JobDescriptionDocumentID}
            style={{ display: 'flex', alignItems: 'center', color: pallet.primary1Color }}
          >
            {fileName}
            <span>
              <FontIcon
                className="material-icons"
                style={{ marginLeft: 5 }}
              > launch
              </FontIcon>
            </span>
          </a>
        </div>
      );
    }

    return (
      <div
        role="button"
        onClick={() => { download(fileById); }}
        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
      >
        <span
          style={{ color: pallet.primary1Color, textDecoration: 'underline' }}
        >
          {`${fileById.Name}${fileById.Extension}`}
        </span>
        <FontIcon
          className="material-icons"
          style={{ marginLeft: 5 }}
        > file_download
        </FontIcon>
      </div>
    );
  };

  useEffect(() => {
    if (JobDescriptionDocumentID !== null) {
      const decoded = decodeURI(JobDescriptionDocumentID);
      const splitted = decoded.split('/');

      if (splitted[0] === 'https:' || splitted[0] === 'http:') {
        const noArguments = splitted[(splitted.length - 1)].split('?');
        setFileName(noArguments[0]);
        setIsSharePoint(true);
      } else {
        dispatch(getFileById(JobDescriptionDocumentID));
        const docName = `${fileById.Name}${fileById.Extension}`;

        setFileName(docName);
        setIsSharePoint(false);
      }
    }
  }, [dispatch]);


  const items = [
    {
      label: <div style={fix} data-cy="EmployeeDetailOrg.OrgCompanyName"><FormattedMessage id="profile.org.company" /></div>,
      value: OrgCompanyName,
      ID: 'EmployeeDetailOrg.OrgCompanyName',
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.org.division" /></div>,
      value: OrgCompanyUnitDivisionName,
      ID: 'EmployeeDetailOrg.OrgCompanyUnitDivisionName',
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.org.unit" /></div>,
      value: OrgCompanyUnitName,
      ID: 'EmployeeDetailOrg.OrgCompanyUnitName',
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.org.position" /></div>,
      value: OrgCompanyPositionName,
      ID: 'EmployeeDetailOrg.OrgCompanyPositionName',
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.org.positionAlt" /></div>,
      value: OrgCompanyPositionAlternativeName,
      ID: 'EmployeeDetailOrg.OrgCompanyPositionAlternativeName',
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.org.location" /></div>,
      value: OrgLocationSub ? `${OrgLocationMain}, ${OrgLocationSub}` : OrgLocationMain,
      ID: 'EmployeeDetailOrg.OrgLocationMain',
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.org.manager" /></div>,
      value: ManagerName,
      ID: 'EmployeeDetailOrg.ManagerName',
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.org.status" /></div>,
      value: language === 'is' ? EmployeeStatusName : getEmployeeStatus(EmployeeStatus),
      ID: 'EmployeeDetailOrg.EmployeeStatusName',
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.org.group" /></div>,
      value: language === 'is' ? EmployeeGroupName : getEmployeeGroup(EmployeeGroup),
      ID: 'EmployeeDetailOrg.EmployeeGroupName',
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.org.employeeDetailID" /></div>,
      value: EmployeeDetailID,
      ID: 'EmployeeDetailOrg.EmployeeDetailID',
    },
  ];

  const shouldShowJobDescription = valuesByKey['Show.EmployeeDetailOrg.JobDescription'];
  if (shouldShowJobDescription !== undefined) {
    if (shouldShowJobDescription.Value === 'true') {
      items.push({
        label: <div style={fix}><FormattedMessage id="profile.org.jobDescription" /></div>,
        value: JobDescriptionDocumentID !== null && showJobDescription(),
        ID: 'EmployeeDetailOrg.JobDescription',
      });
    }
  }

  const costCenterConfig = valuesByKey['Show.EmployeeDetailOrg.OrgCompanyCostCenter'];
  if (costCenterConfig && costCenterConfig.Value) {
    const costCenterIDs = map(costCenterConfig.Value.split`,`, trim);
    items.push({
      label: <div style={fix}><FormattedMessage id="profile.org.costCenter" /></div>,
      value: map(costCenterIDs, x => employeeCompanyConnection[`OrgCompanyCostCenter${x}`]).join` `,
    });
  }

  const show = key => get(valuesByKey, [`Hide.${key}`, 'Value']) !== 'true';

  const dataSource = items.filter(x => x.value && show(x.ID));

  return (
    <DetailList
      containerStyle={{
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
      }}
      dataSource={dataSource}
    />
  );
};

EmployeeCompanyConnectionTable.propTypes = {
  employeeCompanyConnection: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  valuesByKey: PropTypes.object.isRequired,
};

export default EmployeeCompanyConnectionTable;




// WEBPACK FOOTER //
// ./src/components/profile/EmployeeCompanyConnectionTable.js